import React, { useEffect, useRef, useState } from 'react';
import { Tabs, Empty } from 'antd';
import { BellOutlined } from '@ant-design/icons';
import type { TabsProps } from 'antd';
import { useTranslation } from 'react-i18next';
import { useWarehouseContext } from '../../../contexts/WarehouseContext';
import { usePickupSiteV2Gray } from '../../../hooks/usePickupSiteV2Gray';
import NewAddressReviewList from './components/NewAddressReviewList';
import ReviewAddressDetail from './components/ReviewAddressDetail';
import DriverFeedbackList from './components/DriverFeedbackList';
import DriverFeedbackDetail from './components/DriverFeedbackDetail';
import { ReviewPoolItem } from './types/reviewPool';
import { DriverFeedbackItem } from './types/driverFeedback';

/**
 * 地址审核池（揽收站点 2.0 · 需求 1、2）
 *
 * 与「揽收站点管理」并列的运营工作台，内部两个子 Tab：
 *  - 新地址审核（待处理新地址）        —— P6.2 列表 / P6.3 详情+操作
 *  - 司机反馈跟进（未建联·走司机核实）  —— P6.7
 *
 * 灰度：仅当前 hub 在 2.0 灰度名单时可用（前后端同名单，见 usePickupSiteV2Gray）。
 * 数据全 mock，后端接口待技术评审后接入。
 */
interface AddressReviewPoolPageProps {
  /** 外层页签切回本页时 +1：站点管理页的操作可能已消解掉审核池条目，需重查列表 */
  refreshSignal?: number;
}

const AddressReviewPoolPage: React.FC<AddressReviewPoolPageProps> = ({ refreshSignal }) => {
  const { t } = useTranslation();
  const { currentWarehouse } = useWarehouseContext();
  const { express2Enabled } = usePickupSiteV2Gray();
  const [activeTab, setActiveTab] = useState<string>('new-address');
  const [selectedItem, setSelectedItem] = useState<ReviewPoolItem | null>(null);
  const [selectedFeedback, setSelectedFeedback] = useState<DriverFeedbackItem | null>(null);
  // resolve 成功后 +1 触发列表刷新 + 清空选中
  const [reloadToken, setReloadToken] = useState(0);
  // 两个 Tab 的待处理总数（列表回传），用于 Tab 徽标 + 顶部提醒
  const [newCount, setNewCount] = useState(0);
  const [feedbackCount, setFeedbackCount] = useState(0);
  // 顶部提醒专用：仅统计已有司机上门反馈结果（揽收成功/失败待建联）的站点。
  // 与 Tab 徽标的 feedbackCount 口径不同——徽标是待跟进总数，含待上门/上门中（还没有反馈结果）。
  const [newFeedbackCount, setNewFeedbackCount] = useState(0);

  const hubId = (currentWarehouse as any)?.id as number | undefined;

  // 外层页签切回 → 转成内部 reloadToken，两个子列表都监听它重查
  useEffect(() => {
    if (refreshSignal) setReloadToken((t) => t + 1);
  }, [refreshSignal]);

  // 内层页签切换（新地址审核 ⇄ 司机反馈跟进）同样要重查：
  // 两边共享站点建联状态，在一边处理完切过去，另一边的口径已经变了
  const handleTabChange = (key: string) => {
    setActiveTab(key);
    setReloadToken((t) => t + 1);
  };

  /**
   * 已就地更新过的条目（id → patch）。
   *
   * <p>上报/编辑/撤销地址异常只 patch selectedItem，**列表组件里的 items 仍是操作前的快照**；
   * 而卡片点击回传的正是 items 里那个对象（ReviewAddressCard 的 `onClick={() => onSelect?.(item)}`）。
   * 不在这里补一层合并，运营「上报 A → 点开 B 核对地址 → 点回 A」就会看到：
   * 记录卡消失、上报入口重现、二次建联那一步消失，且建站时「其他备注」不再自动填
   * 「异常地址—$原因」—— 最后一条是 PRD 需求点1 明确要求的，等于功能失效。
   *
   * <p>用 ref 而非 state：它只在 handleSelect 的那一刻被读一次，不需要驱动渲染。
   */
  const selectedPatchesRef = useRef<Map<number, Partial<ReviewPoolItem>>>(new Map());

  // 真刷新之后服务端数据才是权威，本地 patch 一律作废 —— 否则会用更旧的 patch 盖掉更新的服务端值。
  // reloadToken 覆盖了全部刷新入口：resolve 完成、内层页签切换、外层页签切回；hubId 覆盖切 hub。
  useEffect(() => {
    selectedPatchesRef.current.clear();
  }, [hubId, reloadToken]);

  /**
   * 选中条目：把该条目上已有的本地 patch 合并回去，避免列表里的陈旧快照覆盖已上报的状态。
   * 列表的「默认选中第一条」与「刷新后回抛最新条目」也都走这里 —— 后者发生在 reloadToken
   * 变化之后，那时 patch 已被上面的 effect 清空，不会拿旧 patch 盖住新数据。
   */
  const handleSelect = (item: ReviewPoolItem) => {
    const patch = selectedPatchesRef.current.get(item.id);
    setSelectedItem(patch ? { ...item, ...patch } : item);
  };

  const handleResolved = () => {
    setSelectedItem(null);
    setReloadToken((t) => t + 1);
  };

  /**
   * 就地更新选中条目（2.2 需求点1 的上报 / 编辑 / 撤销地址异常），**不碰 reloadToken**。
   *
   * <p>上报不是 resolve：条目仍待处理，也没有离开列表口径，所以列表本来就不需要重拉。
   * 反过来，重拉的代价是实打实的 —— reloadToken 会让 NewAddressReviewList 清空 items 并只取
   * 第 1 页，运营滚到第 2 页之后上报，卡片就直接消失了。
   *
   * <p>凭证缩略图不受影响：预签名可读地址由 reportAddressAbnormal 随返回值一并给出
   * （见 graphql/mutations/addressAbnormal.ts），不需要靠重拉列表间接取回。
   *
   * <p>patch 同时记进 selectedPatchesRef —— 列表组件里的 items 仍是操作前的快照，
   * 不记下来，运营点走再点回来就会看到上报记录消失（见该 ref 的说明）。
   */
  const handlePatchSelected = (patch: Partial<ReviewPoolItem>) => {
    if (!selectedItem) return;
    selectedPatchesRef.current.set(selectedItem.id, {
      ...selectedPatchesRef.current.get(selectedItem.id),
      ...patch,
    });
    setSelectedItem((cur) => (cur ? { ...cur, ...patch } : cur));
  };

  // 司机反馈跟进单独一个回调：两个 tab 的选中态互不影响，避免一边操作把另一边已选中的条目重置掉。
  // 必须清 selectedFeedback——否则站点转已建联(4) 后卡片已从列表口径消失，右侧仍渲染陈旧对象、按钮可重复点，
  // 且列表「默认选中第一条」只在 selectedId==null 时触发，不清就不会自动切到下一条。
  const handleFeedbackResolved = () => {
    setSelectedFeedback(null);
    setReloadToken((t) => t + 1);
  };

  // 灰度兜底：非灰度 hub 直接输 URL 也不放行（菜单已隐藏，这里防绕过）
  if (!express2Enabled(hubId)) {
    return <Empty description="当前 HUB 未开启揽收站点 2.0" className="mt-24" />;
  }

  const newAddressTab = (
    <div className="flex h-full gap-4">
      {/* 左：待处理地址列表（P6.2） */}
      <div className="w-[420px] shrink-0 overflow-hidden">
        <NewAddressReviewList
          hubId={hubId}
          reloadToken={reloadToken}
          selectedId={selectedItem?.id}
          onSelect={handleSelect}
          onCountChange={setNewCount}
          onSelectedMissing={() => setSelectedItem(null)}
        />
      </div>
      {/* 右：详情 + 诊断 + 推荐操作（P6.3） */}
      <div className="flex-1 overflow-auto rounded-lg border border-gray-200 p-4">
        {selectedItem ? (
          <ReviewAddressDetail
            item={selectedItem}
            onResolved={handleResolved}
            onPatchSelected={handlePatchSelected}
          />
        ) : (
          <Empty
            description={t('pickup.addressReviewPool.detail.selectHint')}
            className="mt-24"
          />
        )}
      </div>
    </div>
  );

  const tabItems: TabsProps['items'] = [
    {
      key: 'new-address',
      label: (
        <span className="flex items-center gap-2">
          {t('pickup.addressReviewPool.tabs.newAddress')}
          {newCount > 0 && (
            <span className="inline-flex min-w-[20px] justify-center rounded-full bg-gray-700 px-1.5 text-xs leading-5 text-white">
              {newCount > 99 ? '99+' : newCount}
            </span>
          )}
        </span>
      ),
      children: newAddressTab,
    },
    {
      key: 'driver-feedback',
      label: (
        <span className="flex items-center gap-2">
          {t('pickup.addressReviewPool.tabs.driverFeedback')}
          {feedbackCount > 0 && (
            <span className="inline-flex min-w-[20px] justify-center rounded-full bg-orange-500 px-1.5 text-xs leading-5 text-white">
              {feedbackCount > 99 ? '99+' : feedbackCount}
            </span>
          )}
        </span>
      ),
      // P6.7 司机反馈跟进：左列表 + 右详情（现场反馈 + 跟进时间线 + 建联/确认无法建联）
      children: (
        <div className="flex h-full gap-4">
          <div className="w-[420px] shrink-0 overflow-hidden">
            <DriverFeedbackList
              hubId={hubId}
              reloadToken={reloadToken}
              selectedId={selectedFeedback?.id}
              onSelect={setSelectedFeedback}
              onCountChange={setFeedbackCount}
              onNewFeedbackCountChange={setNewFeedbackCount}
              onSelectedMissing={() => setSelectedFeedback(null)}
            />
          </div>
          <div className="flex-1 overflow-auto rounded-lg border border-gray-200 p-4">
            {/* key 强制换卡片时重挂载：详情内的群名称/群ID/联系结果/凭证均为本地 state 且无重置 effect，
                不换 key 会跨卡片残留，导致把上一张卡片的群ID或凭证提交到当前站点 */}
            {selectedFeedback ? (
              <DriverFeedbackDetail
                key={selectedFeedback.id}
                item={selectedFeedback}
                onResolved={handleFeedbackResolved}
              />
            ) : (
              <Empty description={t('pickup.addressReviewPool.detail.selectHint')} className="mt-24" />
            )}
          </div>
        </div>
      ),
      // 随页面挂载即拉取（供顶部提醒/Tab 徽标计数），避免懒渲染导致未激活时拿不到 total
      forceRender: true,
    },
  ];

  return (
    <div className="h-full flex flex-col">
      {/* 顶部提醒：未建联站点收到司机上门反馈，点击跳转「司机反馈跟进」。
          只在确实有反馈结果（揽收成功/失败待建联）时出现——仅「待上门」「上门中」还没反馈可跟进，
          用待跟进总数会误报「收到了司机上门反馈」 */}
      {newFeedbackCount > 0 && activeTab !== 'driver-feedback' && (
        <div className="mb-3 flex items-center justify-between rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5">
          <span className="flex items-center gap-2 text-sm text-amber-800">
            <BellOutlined />
            {t('pickup.addressReviewPool.banner.driverFeedback', { count: newFeedbackCount })}
          </span>
          <a
            className="shrink-0 text-sm font-medium text-amber-700"
            onClick={() => setActiveTab('driver-feedback')}
          >
            {t('pickup.addressReviewPool.banner.goView')} →
          </a>
        </div>
      )}
      <Tabs
        activeKey={activeTab}
        onChange={handleTabChange}
        items={tabItems}
        className="h-full [&_.ant-tabs-content]:h-full [&_.ant-tabs-tabpane]:h-full"
      />
    </div>
  );
};

export default AddressReviewPoolPage;
